import { Action } from 'redux';
// Import States.
import {
	AppState,
	NewBusinessApplicationState,
	PolicyState,
	getInitialNewBusinessApplicationState,
} from '@app/redux/states';
// Import Actions.
import {
	LoadNewBusinessApplicationAction,
	LoadNewBusinessApplicationDocumentAction,
	LoadNewBusinessBindingAuthorityEligibilityResultsAction,
	LoadNewBusinessPossiblePerilsAction,
	LoadPossibleNewBusinessDisclosureTextsAction,
	NewBusinessApplicationAction, 
	SelectNewBusinessBuildingAction,
	SelectNewBusinessMortgageeAction,
	UnselectNewBusinessBuildingAction,
    UnselectNewBusinessMortgageeAction,
} from '@app/redux/actions';
// Import state initializers.
import {
	initializePolicyState
} from '@app/redux/reducers/initializers';
// Import state queries.
import {
	queryPolicyState,
} from '@app/redux/reducers/queries';
// Import api models.
import {
	BindingAuthorityEligibilityResult,
	NewBusinessApplication,
	NewBusinessApplicationDocument,
	PossibleDisclosureText,
	PossiblePeril,
} from '@app/models/api';


/**
 * This handles initializing the state.
 * @param state
 * @param action
 */
let initializeState = (state: AppState, action: NewBusinessApplicationAction): AppState => {
	let newState: AppState = { ...state };
	// Initialize the parent state.
	newState = initializePolicyState(newState, action);
	// Query the parent state.
	let policyState: PolicyState = queryPolicyState(newState, action);
	// Initialize the state.
	if (!policyState.newBusinessApplication) {
		policyState.newBusinessApplication = getInitialNewBusinessApplicationState();
	}
	else {
		policyState.newBusinessApplication = { ...policyState.newBusinessApplication };
	}
	return newState;
}

/**
 * Query the state.
 * @param state
 * @param action
 */
let queryNewBusinessApplicationState = (state: AppState, action: NewBusinessApplicationAction): NewBusinessApplicationState => {
	return queryPolicyState(state, action).newBusinessApplication;
}


// Load New Business Application Reducer.

let LoadNewBusinessApplicationReducer = (state: AppState, action: Action): AppState => {
	let loadNewBusinessApplicationAction: LoadNewBusinessApplicationAction = action as LoadNewBusinessApplicationAction;
	let newBusinessApplication: NewBusinessApplication = loadNewBusinessApplicationAction.newBusinessApplication;
	let newBusinessApplicationDocument: NewBusinessApplicationDocument | undefined = loadNewBusinessApplicationAction.newBusinessApplicationDocument;

	// Create copy of the of app state.
	let newState: AppState = initializeState(state, loadNewBusinessApplicationAction);

	// Query the state.
	let newBusinessApplicationState: NewBusinessApplicationState = queryNewBusinessApplicationState(newState, loadNewBusinessApplicationAction);

	newBusinessApplicationState.newBusinessApplication = newBusinessApplication;
	newBusinessApplicationState.newBusinessApplicationDocument = newBusinessApplicationDocument;
	newBusinessApplicationState.isNewBusinessApplicationLoaded = true;

	return newState;
}


// Select New Business Building Reducer.

let SelectNewBusinessBuildingReducer = (state: AppState, action: Action): AppState => {
	let selectNewBusinessBuildingAction: SelectNewBusinessBuildingAction = action as SelectNewBusinessBuildingAction;
	let buildingGuid: string = selectNewBusinessBuildingAction.buildingGuid;

	// Create copy of the of app state.
	let newState: AppState = initializeState(state, selectNewBusinessBuildingAction);

	// Query the state.
	let newBusinessApplicationState: NewBusinessApplicationState = queryNewBusinessApplicationState(newState, selectNewBusinessBuildingAction);

	newBusinessApplicationState.selectedBuildingGuid = buildingGuid;

	return newState;
}


// Unselect New Business Building Reducer.

let UnselectNewBusinessBuildingReducer = (state: AppState, action: Action): AppState => {
	let unselectNewBusinessBuildingAction: UnselectNewBusinessBuildingAction = action as UnselectNewBusinessBuildingAction;

	// Create copy of the of app state.
	let newState: AppState = initializeState(state, unselectNewBusinessBuildingAction);

	// Query the state.
	let newBusinessApplicationState: NewBusinessApplicationState = queryNewBusinessApplicationState(newState, unselectNewBusinessBuildingAction);

	newBusinessApplicationState.selectedBuildingGuid = undefined;

	return newState;
}


// Select New Business Mortgagee Reducer.

let SelectNewBusinessMortgageeReducer = (state: AppState, action: Action): AppState => {
	let selectNewBusinessMortgageeAction: SelectNewBusinessMortgageeAction = action as SelectNewBusinessMortgageeAction;
	let mortgageeGuid: string = selectNewBusinessMortgageeAction.mortgageeGuid;

	// Create copy of the of app state.
	let newState: AppState = initializeState(state, selectNewBusinessMortgageeAction);

	// Query the state.
	let newBusinessApplicationState: NewBusinessApplicationState = queryNewBusinessApplicationState(newState, selectNewBusinessMortgageeAction);

	newBusinessApplicationState.selectedMortgageeGuid = mortgageeGuid;

	return newState;
}


// Unselect New Business Mortgagee Reducer.

let UnselectNewBusinessMortgageeReducer = (state: AppState, action: Action): AppState => {
	let unselectNewBusinessMortgageeAction: UnselectNewBusinessMortgageeAction = action as UnselectNewBusinessMortgageeAction;

	// Create copy of the of app state.
	let newState: AppState = initializeState(state, unselectNewBusinessMortgageeAction);

	// Query the state.
	let newBusinessApplicationState: NewBusinessApplicationState = queryNewBusinessApplicationState(newState, unselectNewBusinessMortgageeAction);

	newBusinessApplicationState.selectedMortgageeGuid = undefined;

	return newState;
}


// Load Possible New Business Disclosure Texts Reducer.

let LoadPossibleNewBusinessDisclosureTextsReducer = (state: AppState, action: Action): AppState => {
	let loadPossibleNewBusinessDisclosureTextsAction: LoadPossibleNewBusinessDisclosureTextsAction = action as LoadPossibleNewBusinessDisclosureTextsAction;
	let possibleDisclosureTexts: PossibleDisclosureText[] = loadPossibleNewBusinessDisclosureTextsAction.possibleDisclosureTexts;

	// Create copy of the of app state.
	let newState: AppState = initializeState(state, loadPossibleNewBusinessDisclosureTextsAction);

	// Query the state.
	let newBusinessApplicationState: NewBusinessApplicationState = queryNewBusinessApplicationState(newState, loadPossibleNewBusinessDisclosureTextsAction);

	newBusinessApplicationState.possibleDisclosureTexts = possibleDisclosureTexts;

	return newState;
}


// Load New Business Application Document Reducer.

let LoadNewBusinessApplicationDocumentReducer = (state: AppState, action: Action): AppState => {
	let loadNewBusinessApplicationDocumentAction: LoadNewBusinessApplicationDocumentAction = action as LoadNewBusinessApplicationDocumentAction;
	let newBusinessApplicationDocument: NewBusinessApplicationDocument = loadNewBusinessApplicationDocumentAction.newBusinessApplicationDocument;

	// Create copy of the of app state.
	let newState: AppState = initializeState(state, loadNewBusinessApplicationDocumentAction);

	// Query the state.
	let newBusinessApplicationState: NewBusinessApplicationState = queryNewBusinessApplicationState(newState, loadNewBusinessApplicationDocumentAction);

	newBusinessApplicationState.newBusinessApplicationDocument = newBusinessApplicationDocument;

	return newState;
}

// Load New Business Binding Authority Eligibility Results Reducer.

let LoadNewBusinessBindingAuthorityEligibilityResultsReducer = (state: AppState, action: Action): AppState => {
	let loadNewBusinessApplicationDocumentAction: LoadNewBusinessBindingAuthorityEligibilityResultsAction = action as LoadNewBusinessBindingAuthorityEligibilityResultsAction;
	let bindingAuthorityEligibilityResults: BindingAuthorityEligibilityResult[] = loadNewBusinessApplicationDocumentAction.bindingAuthorityEligibilityResults;

	// Create copy of the of app state.
	let newState: AppState = initializeState(state, loadNewBusinessApplicationDocumentAction);

	// Query the state.
	let newBusinessApplicationState: NewBusinessApplicationState = queryNewBusinessApplicationState(newState, loadNewBusinessApplicationDocumentAction);

	newBusinessApplicationState.bindingAuthorityEligibilityResults = bindingAuthorityEligibilityResults;
	newBusinessApplicationState.areBindingAuthorityEligibilityResultsLoaded = true;

	return newState;
}

// Load New Business Possible Perils Reducer.

let LoadNewBusinessPossiblePerilsReducer = (state: AppState, action: Action): AppState => {
	let loadNewBusinessPossiblePerilsAction: LoadNewBusinessPossiblePerilsAction = action as LoadNewBusinessPossiblePerilsAction;
	let newBusinessPossiblePerils: PossiblePeril[] = loadNewBusinessPossiblePerilsAction.newBusinessPossiblePerils;

	// Create copy of the of app state.
	let newState: AppState = initializeState(state, loadNewBusinessPossiblePerilsAction);

	// Query the state.
	let newBusinessApplicationState: NewBusinessApplicationState = queryNewBusinessApplicationState(newState, loadNewBusinessPossiblePerilsAction);

	newBusinessApplicationState.areNewBusinessPossiblePerilsLoaded = true;
	newBusinessApplicationState.newBusinessPossiblePerils = newBusinessPossiblePerils;

	return newState;
}

// Export reducers.
export {
	LoadNewBusinessApplicationReducer,
	SelectNewBusinessBuildingReducer,
	UnselectNewBusinessBuildingReducer,
	SelectNewBusinessMortgageeReducer,
	UnselectNewBusinessMortgageeReducer,
	LoadPossibleNewBusinessDisclosureTextsReducer,
	LoadNewBusinessApplicationDocumentReducer,
	LoadNewBusinessBindingAuthorityEligibilityResultsReducer,
	LoadNewBusinessPossiblePerilsReducer,
};

// Export initializers.
export {
	initializeState
}

// Export queries.
export {
	queryNewBusinessApplicationState
};